import { AnimatePresence } from 'framer-motion';
import { TransactionsCommonMappingModalProps } from './props';
import { TransactionsCommonMappingModalLayout } from './Layout';
import { TransactionsCommonMappingModalContentBoard } from './Content/Board';
import { TransactionsCommonMappingModalContentDetails } from './Content/Details';
import { ReactFlowProvider } from 'reactflow';

/**
 * TransactionsCommonMappingModal Component
 * This component is responsible for rendering a modal with transaction mapping content.
 * It uses the AnimatePresence component from framer-motion to handle animations when the modal is opened or closed.
 * The modal layout and content are provided by TransactionsCommonMappingModalLayout,
 * TransactionsCommonMappingModalContentBoard, and TransactionsCommonMappingModalContentDetails components.
 *
 * @param {TransactionsCommonMappingModalProps} props - The properties for the TransactionsCommonMappingModal component.
 * @param {Function} props.onClose - Callback function to handle closing the modal.
 * @param {boolean} props.open - Flag indicating whether the modal is open.
 * @param {DOMRect | undefined} props.boundings - The bounding rectangle of the button for modal positioning.
 *
 * @returns {JSX.Element} The rendered component displaying the modal with transaction mapping content.
 *
 * @example
 * // Usage example:
 * <TransactionsCommonMappingModal
 *   onClose={() => function('Modal closed')}
 *   open={true}
 *   boundings={buttonRef.current?.getBoundingClientRect()}
 * />
 */
export function TransactionsCommonMappingModal({
  onClose,
  open,
  boundings,
}: TransactionsCommonMappingModalProps) {
  return (
    <AnimatePresence mode="popLayout">
      {open && (
        <TransactionsCommonMappingModalLayout
          onClose={onClose}
          boundings={boundings}
        >
          <ReactFlowProvider>
            <TransactionsCommonMappingModalContentBoard />
          </ReactFlowProvider>
          <TransactionsCommonMappingModalContentDetails onClose={onClose} />
        </TransactionsCommonMappingModalLayout>
      )}
    </AnimatePresence>
  );
}
